import React from 'react';
import { Users, Box, TrendingUp, CheckCircle2 } from 'lucide-react';

export default function MbogiShowcase() {
  return (
    <div className="bg-[#fdfcf9] font-sans text-[#0a1d2e]">
      
      {/* SECTION: BUILT BY THE MBOGI */}
      <section className="py-16 md:py-10 px-6">
        <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Content */}
          <div className="order-2 lg:order-1">
            <h2 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
              Built by the Mbogi, <br />
              For the Mbogi.
            </h2>
            <p className="text-gray-500 text-lg mb-8 leading-relaxed">
              In Swahili, "Mbogi" means your crew, your squad, your community. 
              We are building a platform where the fans own the success of the 
              stories they love. No more gatekeepers, just creators and their supporters.
            </p>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-green-500 fill-green-50/50" />
                </div>
                <div>
                  <h4 className="font-bold text-lg">Transparent Earnings</h4>
                  <p className="text-gray-500 text-sm">Every cent tracked via on-chain smart contracts.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-green-500 fill-green-50/50" />
                </div>
                <div>
                  <h4 className="font-bold text-lg">Global Distribution</h4>
                  <p className="text-gray-500 text-sm">Access to worldwide streaming and cinema circuits.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Image Masonry */}
          <div className="order-1 lg:order-2 grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <img 
                src="https://images.unsplash.com/photo-1533488765986-dfa2a9939acd?auto=format&fit=crop&q=80&w=400" 
                alt="Filmmaker" 
                className="w-full h-64 object-cover rounded-3xl" 
              />
              <img 
                src="https://images.unsplash.com/photo-1511632765486-a01980e01a18?auto=format&fit=crop&q=80&w=400" 
                alt="Community" 
                className="w-full h-80 object-cover rounded-3xl" 
              />
            </div>
            <div className="space-y-4 pt-8">
              <img 
                src="https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&q=80&w=400" 
                alt="Camera Gear" 
                className="w-full h-56 object-cover rounded-3xl" 
              />
              <div className="bg-[#f3e8d6] w-full h-64 rounded-3xl flex items-center justify-center p-6 text-center">
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold opacity-40 mb-2">Creators</p>
                  <div className="flex justify-center -space-x-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="w-8 h-8 rounded-full border-2 border-[#f3e8d6] bg-gray-400" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION: THE MBOGI SOLUTION */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">The Mbogi Solution</h2>
            <p className="text-gray-500 leading-relaxed">
              We are building the first crowd-investing platform dedicated to the African film industry.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
            {[
              {
                title: "Democratized Access",
                desc: "Anyone can become a film financier. Start with as little as $100 and own a piece of the next hit series.",
                icon: <Users className="w-5 h-5" />
              },
              {
                title: "Asset Tokenization",
                desc: "We use blockchain to ensure transparency in royalty distribution and project milestones.",
                icon: <Box className="w-5 h-5" />
              },
              {
                title: "Shared Returns",
                desc: "When the movie succeeds—via streaming, theaters, or merch—you earn. Direct profit sharing for the community.",
                icon: <TrendingUp className="w-5 h-5" />
              }
            ].map((card, idx) => (
              <div key={idx} className="bg-[#fdfcf9] border border-gray-100 p-8 rounded-[2.5rem] hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 bg-[#fdf4e7] text-[#c07d3e] rounded-2xl flex items-center justify-center mb-6">
                  {card.icon}
                </div>
                <h3 className="text-xl font-bold mb-4">{card.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 py-12 border-t border-gray-100">
            {[
              { val: "$20B+", label: "Global Afrobeats/Film Market" },
              { val: "1.2K", label: "Waitlist Signups" },
              { val: "15+", label: "Studios Partnered" },
              { val: "100%", label: "Transparency" }
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-[#c07d3e] mb-1">{stat.val}</p>
                <p className="text-[10px] md:text-xs uppercase tracking-widest font-bold text-gray-400 leading-tight">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}